import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import BusinessHours from '@/components/page-components/project/agents/BusinessHours';
import AvailabilityOverrides from '@/components/page-components/project/agents/AvailabilityOverrides';
import { ServicesManager } from '@/components/page-components/project/agents/business/ServicesManager';
import BusinessInfoFields from '@/components/page-components/project/agents/ui/BusinessInfoFields';
import StickySaveBar from '@/components/common/StickySaveBar';
import type { AvailabilityOverride, BusinessHour } from '@/services/api';
import type { Service } from '@/types/Service';
import type { BusinessInfoFormValue } from '@/utils/businessInfo';

export const BusinessTabSkeleton: React.FC = () => (
  <div className="mx-auto flex w-full max-w-4xl flex-col gap-6">
    <Skeleton className="h-36 w-full rounded-lg" />
    <Skeleton className="h-44 w-full rounded-lg" />
    <Skeleton className="h-72 w-full rounded-lg" />
  </div>
);

interface AgentBusinessTabPanelProps {
  agentId: number;
  ready: boolean;
  businessHours: BusinessHour[];
  isBusinessHoursLoading: boolean;
  services: Service[];
  isServicesLoading: boolean;
  onServicesChange: (services: Service[]) => void;
  availabilityOverrides: AvailabilityOverride[];
  isOverridesLoading: boolean;
  onOverridesChange: (overrides: AvailabilityOverride[]) => void;
  showAvailabilityOverrides?: boolean;
  businessInfoForm: BusinessInfoFormValue;
  onBusinessInfoChange: (value: BusinessInfoFormValue) => void;
  isSavingBusiness: boolean;
  isBusinessDirty: boolean;
  onBusinessHoursChange: (hours: BusinessHour[]) => void;
  onReset: () => void;
  onSave: () => void;
  disabled?: boolean;
  crmEnabled?: boolean;
}

export const AgentBusinessTabPanel: React.FC<AgentBusinessTabPanelProps> = ({
  agentId,
  ready,
  businessHours,
  isBusinessHoursLoading,
  services,
  isServicesLoading,
  onServicesChange,
  availabilityOverrides,
  isOverridesLoading,
  onOverridesChange,
  showAvailabilityOverrides = true,
  businessInfoForm,
  onBusinessInfoChange,
  isSavingBusiness,
  isBusinessDirty,
  onBusinessHoursChange,
  onReset,
  onSave,
  disabled = false,
  crmEnabled = false,
}) => {
  const { t } = useTranslation();

  if (!ready) {
    return <BusinessTabSkeleton />;
  }

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-6 animate-in fade-in-0 duration-200">
      <Card className="w-full gap-4 space-y-4 bg-card p-4 sm:space-y-6 sm:p-6">
        <div className="px-4 sm:px-6">
          <CardTitle
            description={t(
              'agent.business.profile.description.translation',
              'Facts callers usually ask for. Used in the agent prompt.'
            )}
          >
            {t(
              'agent.business.profile.title.translation',
              'Business information'
            )}
          </CardTitle>
        </div>
        <BusinessInfoFields
          value={businessInfoForm}
          onChange={onBusinessInfoChange}
          disabled={disabled}
        />
      </Card>
      <Card className="w-full gap-4 space-y-4 bg-card p-4 sm:space-y-6 sm:p-6">
        <ServicesManager
          agentId={agentId}
          services={services}
          isLoading={isServicesLoading}
          onServicesChange={onServicesChange}
          crmEnabled={crmEnabled}
        />
      </Card>
      {showAvailabilityOverrides && (
        <Card className="w-full gap-4 space-y-4 bg-card p-4 sm:space-y-6 sm:p-6">
          <AvailabilityOverrides
            agentId={agentId}
            overrides={availabilityOverrides}
            isLoading={isOverridesLoading}
            onOverridesChange={onOverridesChange}
          />
        </Card>
      )}
      <Card className="w-full gap-4 space-y-4 bg-card p-4 sm:space-y-6 sm:p-6">
        <BusinessHours
          key={agentId}
          agentId={agentId}
          value={businessHours}
          onChange={onBusinessHoursChange}
          isLoading={isBusinessHoursLoading}
          disabled={disabled}
        />
      </Card>
      <StickySaveBar
        hidden={disabled}
        isDirty={isBusinessDirty}
        isSaving={isSavingBusiness}
        onSave={onSave}
        onReset={onReset}
        saveLabel={t(
          'agent.business.save.translation',
          'Save business settings'
        )}
      />
    </div>
  );
};
